import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { STORAGE_URL } from "../../services/api"; 
import toast from "react-hot-toast";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import { Plus, Trash2, Video, Link as LinkIcon, Edit, X, Save } from "lucide-react";

export default function Videos() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🌟 Edit Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // 🌟 Edit Form Fields States
  const [editTitle, setEditTitle] = useState("");
  const [editStatus, setEditStatus] = useState("active");
  const [editVideoUrl, setEditVideoUrl] = useState("");
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editThumbnail, setEditThumbnail] = useState(null);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await api.get("/videos", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVideos(response.data.data);
    } catch (error) {
      console.error("Error fetching videos:", error);
      toast.error("Failed to load videos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        const token = localStorage.getItem("admin_token");
        await api.delete(`/videos/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Video deleted successfully");
        setVideos(videos.filter(v => v.id !== id));
      } catch (error) {
        toast.error("Failed to delete video");
      }
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
        const token = localStorage.getItem("admin_token");
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        
        await api.put(`/videos/${id}/status`, { status: newStatus }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        
        toast.success(`Video marked as ${newStatus}`);
        setVideos(videos.map(v => v.id === id ? { ...v, status: newStatus } : v));
    } catch (error) {
        toast.error("Failed to update status");
    }
  };

  // 🌟 Modal Open Karne ka logic
  const handleEditClick = (video) => {
    setEditingVideo(video);
    setEditTitle(video.title || "");
    setEditStatus(video.status || "active");
    setEditVideoUrl(video.video_url || "");
    setEditVideoFile(null); // Purani file dikha nahi sakte input me
    setEditThumbnail(null); // Purani file dikha nahi sakte input me
    setIsEditModalOpen(true);
  };

  // 🌟 Update Data Submit karne ka logic
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      const token = localStorage.getItem("admin_token");
      const formData = new FormData();
      
      // Laravel me file update karne ke liye POST ke sath _method=PUT bhejna padta hai
      formData.append('_method', 'PUT'); 
      formData.append('title', editTitle);
      formData.append('status', editStatus);
      if (editVideoUrl) formData.append('video_url', editVideoUrl);
      if (editVideoFile) formData.append('video_file', editVideoFile);
      if (editThumbnail) formData.append('thumbnail', editThumbnail);

      const response = await api.post(`/videos/${editingVideo.id}`, formData, {
        headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data' 
        },
      });

      toast.success("Video updated successfully!");
      // Table data bina refresh kiye update kar do
      setVideos(videos.map(v => v.id === editingVideo.id ? response.data.data : v));
      setIsEditModalOpen(false); // Modal close karein
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update video");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <PageTitle title="Manage Videos" subtitle="Add, update, or remove videos for the frontend." />
        
        <button
          onClick={() => navigate("/videos/new")}
          className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-6 rounded-lg transition-colors cursor-pointer"
        >
          <Plus size={18} /> Add New Video
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow border border-gray-200 overflow-hidden relative">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider font-bold">
                            <th className="py-4 px-6">Thumbnail</th>
                            <th className="py-4 px-6">Video Details</th>
                            <th className="py-4 px-6">Source</th>
                            <th className="py-4 px-6 text-center">Status</th>
                            <th className="py-4 px-6 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 text-sm">
                        {videos.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="py-8 text-center text-gray-500">No videos found. Click "Add New Video" to create one.</td>
                            </tr>
                        ) : (
                            videos.map(video => (
                                <tr key={video.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="py-4 px-6">
                                        <div className="w-24 h-14 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                            {video.thumbnail ? (
                                                <img src={STORAGE_URL(video.thumbnail)} alt="thumb" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-4 px-6 font-bold text-gray-900">{video.title}</td>
                                    <td className="py-4 px-6">
                                        {video.video_url ? (
                                            <a href={video.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-600 font-semibold hover:underline text-xs">
                                                <LinkIcon size={14}/> Link
                                            </a>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold text-xs">
                                                <Video size={14}/> Uploaded File
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-4 px-6 text-center">
                                        <button 
                                            onClick={() => handleStatusToggle(video.id, video.status)}
                                            className={`px-3 py-1 text-xs font-bold rounded-full cursor-pointer transition-colors border ${video.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}
                                        >
                                            {video.status === 'active' ? 'Active' : 'Inactive'}
                                        </button>
                                    </td>
                                    <td className="py-4 px-6 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            {/* 🌟 FIX: onClick change kiya */}
                                            <button 
                                                onClick={() => handleEditClick(video)} 
                                                className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl transition-colors cursor-pointer" 
                                                title="Edit Video"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(video.id, video.title)} 
                                                className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition-colors cursor-pointer" 
                                                title="Delete Video"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
      )}

      {/* 🌟 EDIT MODAL UI */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
                
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                        <Edit className="text-blue-500" size={18} /> Edit Video
                    </h3>
                    <button 
                        onClick={() => setIsEditModalOpen(false)} 
                        className="text-gray-400 hover:text-gray-900 transition-colors cursor-pointer"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Modal Body / Form */}
                <form onSubmit={handleUpdateSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Video Title *</label>
                        <input 
                            type="text" 
                            required 
                            value={editTitle} 
                            onChange={(e) => setEditTitle(e.target.value)} 
                            className="w-full px-4 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                            placeholder="e.g. How to crack interviews"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">YouTube / External URL</label>
                        <input 
                            type="url" 
                            value={editVideoUrl} 
                            onChange={(e) => setEditVideoUrl(e.target.value)} 
                            className="w-full px-4 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                            placeholder="Leave empty if uploading file manually"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Upload New Thumbnail</label>
                            <input 
                                type="file" 
                                accept="image/*"
                                onChange={(e) => setEditThumbnail(e.target.files[0])} 
                                className="w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 cursor-pointer"
                            />
                            <p className="text-[10px] text-gray-400 mt-1">Leave empty to keep current thumbnail</p>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Upload New Video</label>
                            <input 
                                type="file" 
                                accept="video/*"
                                onChange={(e) => setEditVideoFile(e.target.files[0])} 
                                className="w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 cursor-pointer"
                            />
                            <p className="text-[10px] text-gray-400 mt-1">Leave empty to keep current video</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Status</label>
                        <select 
                            value={editStatus} 
                            onChange={(e) => setEditStatus(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>

                    {/* Modal Footer */}
                    <div className="pt-4 mt-2 border-t border-gray-100 flex justify-end gap-3">
                        <button 
                            type="button" 
                            onClick={() => setIsEditModalOpen(false)}
                            className="px-5 py-2.5 text-sm font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={isUpdating}
                            className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-black bg-yellow-500 rounded-xl hover:bg-yellow-600 transition-colors cursor-pointer disabled:opacity-50"
                        >
                            <Save size={16} /> {isUpdating ? 'Saving...' : 'Update Video'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </AdminLayout>
  );
}